// =============================================================================
// Person-anchored bulk stakeholder management. Given ONE stakeholder profile, manage across many projects:
//   • Membership   — add/remove the profile as a project stakeholder (checkbox per project; removal
//                    unassigns any tasks it owned). Filter by portfolio / state / search.
//   • Assignments  — own one or more task templates (e.g. "Roof Survey") across the member projects; a
//                    Buckets multi-select narrows the pickable task templates by their bucket (role).
//   • Defaults     — the task templates this person picks up AUTOMATICALLY on every new project of a
//                    vertical. Per-vertical, stored on the profile, and applied once at project creation;
//                    "Backfill now" applies them to the projects that already exist.
//   • Matrix       — (full mode) every template × member project at once.
// Data is loaded on demand via admin methods (the org projects × tasks volume is too big to publish live).
// Used compact inside the Stakeholder modal and full on the Team page.
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { RichMultiSelect } from '/imports/ui/RichMultiSelect';
import { Checkbox } from '/imports/ui/Checkbox';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { DividerTabs } from '/imports/ui/DividerTabs';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { Icon } from '/imports/ui/Icon';
import { Tooltip } from 'react-tooltip';
import { toastOnError } from '/imports/ui/utils';
import { uniqueArray } from '/imports/utils';
import {
  ProjectManagementStakeholderBulkData, ProjectManagementBulkProject, ProjectManagementBulkTemplate,
  ProjectManagementDefaultCoverageRow,
  projectManagementStakeholderBulkData, projectManagementProfileTemplateOwnership,
  projectManagementSetProfileMemberships, projectManagementSetProfileTaskAssignments,
  projectManagementLifecycleTemplates, projectManagementSetProfileDefaults,
  projectManagementDefaultCoverage, projectManagementBackfillProfileDefaults,
} from '/imports/api/projectManagement';
import { projectManagementLifecycles } from '/imports/api/projectManagementLifecycles';

const PartyBulkManagerStyled = styled.div`
  .main-tabs { margin-bottom: 1rem; }
  .bm-filters { display: flex; gap: 0.6rem; flex-wrap: nowrap; align-items: center; margin-bottom: 0.75rem; }
  .bm-filters select, .bm-filters .search, .bm-filters .bm-tmpl-select, .bm-filters .bm-bucket-select { flex: 1 1 0; min-width: 0; }
  .bm-bulk { display: flex; gap: 0.6rem; flex-wrap: wrap; align-items: center; margin-bottom: 0.75rem; }
  .bm-count { color: ${css.grey3}; font-size: 0.85rem; margin-left: auto; }
  .bm-scroll { max-height: 24rem; overflow: auto; border: ${css.inputBorder}; border-radius: ${css.inputBorderRadius}; }
  table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  th, td { text-align: left; padding: 0.45rem 0.6rem; border-bottom: 1px solid ${css.grey6}; white-space: nowrap; }
  th { position: sticky; top: 0; background: ${css.inputBackgroundColor}; z-index: 1; color: ${css.grey3}; font-weight: 600; }
  /* An open dropdown has to clear the sticky header below it. RichMultiSelect ships .options at z-index 1,
     which only TIES with the header above, and a tie is broken by document order — the table comes later,
     so it would paint over the open panel. Raising .options is the fix rather than lowering the header,
     which needs its own z-index to stay above its table (the matrix's sticky first column is already 1).
     It must land on .options: .scrollable-options inside it is position:static, so a z-index there is
     ignored outright. */
  .bm-filters .options { z-index: 2; }
  .bm-matrix-tools .options { z-index: 3; }  /* the matrix header row is itself 2 */
  td.check, th.check { text-align: center; width: 2.5rem; }
  .sub { color: ${css.grey3}; font-size: 0.8rem; }
  .muted { color: ${css.grey4}; }
  .pill { display: inline-block; padding: 0.05rem 0.4rem; border-radius: 1rem; background: ${css.grey6}; color: ${css.grey2}; font-size: 0.75rem; margin-left: 0.35rem; }
  .bm-empty { color: ${css.grey3}; padding: 1.5rem; text-align: center; }
  /* Defaults: the hint earns its space — auto-assignment is invisible once it happens, so the rules are
     stated where they are set. The "also default for" names wrap rather than widen the table. */
  .bm-hint { color: ${css.grey3}; font-size: 0.82rem; margin: 0 0 0.75rem; max-width: 46rem; }
  .bm-defaults td.others { white-space: normal; color: ${css.grey3}; font-size: 0.8rem; }
  /* The task names are sentences, not labels: let them wrap so the whole row fits the panel and the
     rightmost column stays on screen, rather than pushing it behind a horizontal scroll. */
  .bm-defaults td.task { white-space: normal; }
  .bm-defaults .bm-vertical-select { flex: 0 1 12rem; }
  /* Alone on its row (no template select beside it, unlike the Assignment tab) this would take the whole
     width. */
  .bm-defaults .bm-bucket-select { flex: 0 1 18rem; }
  .matrix-scroll { max-height: 28rem; overflow: auto; border: ${css.inputBorder}; border-radius: ${css.inputBorderRadius}; }
  /* Separate (not collapsed) borders so the sticky Project column + header row keep their border-bottom —
     with border-collapse:collapse the collapsed borders detach from sticky cells and drop out on scroll. */
  .matrix { border-collapse: separate; border-spacing: 0; }
  /* Rotated 180°, so the header/body divider is border-TOP (border-bottom would land at the visual top). */
  .matrix th.tmpl { writing-mode: vertical-rl; transform: rotate(180deg); max-height: 9rem; vertical-align: bottom; font-weight: 500; border-top: 1px solid ${css.grey6}; border-bottom: 0; }
  .matrix td.proj { position: sticky; left: 0; z-index: 1; background: ${css.inputBackgroundColor}; border-right: 1px solid ${css.grey6}; }
  .matrix thead th:first-child { position: sticky; left: 0; z-index: 2; border-right: 1px solid ${css.grey6}; }
  /* Per-column select-all strip: toggles a whole task column across every project. */
  .matrix tr.select-all-row td { background: ${css.grey8}; border-bottom: 1px solid ${css.grey6}; }
  .matrix tr.select-all-row td.proj { color: ${css.grey3}; font-size: 0.8rem; }
  .bm-matrix-tools { margin-bottom: 0.75rem; display: flex; gap: 0.6rem; align-items: center; }
  .bm-matrix-tools .bm-matrix-bucket-select { flex: 0 1 16rem; }
  /* Full mode (Team page): fill the detail pane as a flex column so only the table scrolls, never the page.
     Compact mode (modal) keeps the fixed max-heights above — the modal is already a bounded container. */
  &.full { display: flex; flex-direction: column; min-height: 0; flex: 1; }
  &.full .bm-scroll, &.full .matrix-scroll { flex: 1 1 0; min-height: 6rem; max-height: none; }
  /* Fullscreen: fixed full-viewport overlay; the matrix-scroll (flex:1) auto-scrolls within it. */
  &.fullscreen { position: fixed; inset: 0; z-index: 1000; background: ${css.white}; padding: 1.5rem 2.5rem; overflow: hidden; }
  .bm-fs-close { position: absolute; top: 0.85rem; right: 1.25rem; z-index: 1; cursor: pointer; color: ${css.grey2}; }
  .bm-matrix-tools .bm-fs-expand { margin-left: auto; cursor: pointer; color: ${css.grey3}; }
`;

interface PartyBulkManagerProps {
  profileId: string;
  mode?: 'compact' | 'full';
  onChange?: () => void;  // membership/assignment changed — let a parent refresh its own view
}

type Cell = { taskId: string; owned: boolean };

/** Bulk-manage one stakeholder profile's project memberships and task assignments. */
export const PartyBulkManager = (props: PartyBulkManagerProps) => {
  const { profileId, mode = 'compact', onChange } = props;
  const [data, setData] = useState<ProjectManagementStakeholderBulkData>();
  const [tab, setTab] = useState<'membership' | 'assignment' | 'defaults' | 'matrix'>('membership');
  const [busy, setBusy] = useState(false);

  // Membership filters
  const [portfolioFilter, setPortfolioFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [search, setSearch] = useState('');

  // Assignment — multiple task templates at once
  const [templateKeys, setTemplateKeys] = useState<string[]>([]);
  const [assignCells, setAssignCells] = useState<Record<string, Record<string, Cell>>>({});  // templateKey -> projectId -> cell
  const [assignBuckets, setAssignBuckets] = useState<string[]>([]);  // narrows the pickable task templates by bucket (role)

  // Defaults — per-vertical, so every piece of it is scoped by the chosen lifecycle
  const [defaultsLifecycle, setDefaultsLifecycle] = useState('');
  const [defaultTemplates, setDefaultTemplates] = useState<ProjectManagementBulkTemplate[]>([]);
  const [defaultKeys, setDefaultKeys] = useState<string[]>([]);
  const [coverage, setCoverage] = useState<ProjectManagementDefaultCoverageRow[]>([]);
  const [defaultBuckets, setDefaultBuckets] = useState<string[]>([]);
  const [confirmBackfill, setConfirmBackfill] = useState(false);

  // Matrix: templateKey -> projectId -> cell
  const [matrix, setMatrix] = useState<Record<string, Record<string, Cell>>>({});
  const [matrixBuckets, setMatrixBuckets] = useState<string[]>([]);  // filter matrix columns by task bucket (role)
  const [fullscreen, setFullscreen] = useState(false);  // matrix expanded to a fixed full-viewport overlay
  const [confirmRemove, setConfirmRemove] = useState<string[]>();  // pending remove-all-filtered ids

  const load = async () => {
    const res = await projectManagementStakeholderBulkData.call({ profileId });
    setData(res);
    return res;
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [profileId]);

  const memberProjectIds = useMemo(() => new Set((data?.memberships || []).map((m) => m.projectManagementProjectId)), [data]);
  const projectById = useMemo(() => new Map((data?.projects || []).map((p) => [p._id, p])), [data]);
  const states = useMemo(() => uniqueArray((data?.projects || []).map((p) => p.state).filter(Boolean) as string[]).sort(), [data]);
  // The task buckets (roles) present across the templates — shared by the Assignment tab's Buckets filter
  // and the Matrix column filter. templates arrive in Tasks-panel order (bucket, then task), so first-
  // occurrence order here yields the buckets in that same panel order.
  const buckets = useMemo(() => uniqueArray((data?.templates || []).map((t) => t.role).filter(Boolean) as string[]), [data]);
  // Assignment tab: the task templates pickable in the "Select tasks…" select, narrowed by the Buckets filter.
  const assignTemplates = useMemo(() => (data?.templates || []).filter((t) => !assignBuckets.length || (t.role != null && assignBuckets.includes(t.role))), [data, assignBuckets]);
  const matrixTemplates = useMemo(() => (data?.templates || []).filter((t) => !matrixBuckets.length || (t.role != null && matrixBuckets.includes(t.role))), [data, matrixBuckets]);

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.projects || []).filter((p) => {
      if (portfolioFilter && p.portfolioId !== portfolioFilter) return false;
      if (stateFilter && p.state !== stateFilter) return false;
      if (q && !(`${p.name} ${p.externalId || ''} ${p.address || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [data, portfolioFilter, stateFilter, search]);

  // ---- Membership ----------------------------------------------------------
  const toggleMembership = (projectId: string, checked: boolean) => toastOnError(async () => {
    setBusy(true);
    try {
      await projectManagementSetProfileMemberships.call(checked
        ? { profileId, addProjectIds: [projectId] }
        : { profileId, removeProjectIds: [projectId] });
      await load();
      onChange?.();
    }
    finally { setBusy(false); }
  })();

  const applyMembershipDelta = (add: boolean, ids: string[]) => toastOnError(async () => {
    setBusy(true);
    try {
      const res = await projectManagementSetProfileMemberships.call(add ? { profileId, addProjectIds: ids } : { profileId, removeProjectIds: ids });
      toast.success(add ? `${strings.Added()} ${res.added}` : `${strings.Removed()} ${res.removed}`);
      await load();
      onChange?.();
    }
    finally { setBusy(false); }
  })();

  const bulkMembership = (add: boolean) => {
    const ids = filteredProjects
      .filter((p) => (add ? !memberProjectIds.has(p._id) : memberProjectIds.has(p._id)))
      .map((p) => p._id);
    if (!ids.length) { toast.info(strings.NothingToDo()); return; }
    if (add) applyMembershipDelta(true, ids);
    else setConfirmRemove(ids);  // confirm before removing — it unassigns any tasks they own
  };

  // ---- Assignment (multiple task templates) -------------------------------
  const loadCells = async (keys: string[]) => {
    if (!keys.length) { setAssignCells({}); return; }
    const entries = await Promise.all(keys.map(async (k) => {
      const rows = await projectManagementProfileTemplateOwnership.call({ profileId, templateKey: k });
      const m: Record<string, Cell> = {};
      for (const r of rows) m[r.projectManagementProjectId] = { taskId: r.taskId, owned: r.owned };
      return [k, m] as const;
    }));
    setAssignCells(Object.fromEntries(entries));
  };
  const selectTemplates = (keys: string[]) => { setTemplateKeys(keys); loadCells(keys); };

  // Narrowing the Buckets filter prunes any already-selected task templates that fall outside it, so the
  // "Select tasks…" widget, its label summary, and the assignment table stay consistent with what's visible.
  const changeAssignBuckets = (bucketVals: string[]) => {
    setAssignBuckets(bucketVals);
    const allowed = new Set((data?.templates || []).filter((t) => !bucketVals.length || (t.role != null && bucketVals.includes(t.role))).map((t) => t.templateKey));
    const pruned = templateKeys.filter((k) => allowed.has(k));
    if (pruned.length !== templateKeys.length) selectTemplates(pruned);
  };

  // Per project, over the SELECTED templates that actually exist there: how many are owned, and whether all are.
  const projectAssignState = (projectId: string) => {
    const present = templateKeys.filter((k) => assignCells[k]?.[projectId]);
    const owned = present.filter((k) => assignCells[k][projectId].owned).length;
    return { present: present.length, owned, allOwned: present.length > 0 && owned === present.length };
  };

  const toggleAssignment = (projectId: string, checked: boolean) => toastOnError(async () => {
    setBusy(true);
    try {
      await projectManagementSetProfileTaskAssignments.call(checked
        ? { profileId, templateKeys, assignProjectIds: [projectId] }
        : { profileId, templateKeys, unassignProjectIds: [projectId] });
      await loadCells(templateKeys);
      onChange?.();
    }
    finally { setBusy(false); }
  })();

  const bulkAssign = (assign: boolean) => toastOnError(async () => {
    if (!templateKeys.length) return;
    const ids = [...memberProjectIds];
    if (!ids.length) { toast.info(strings.NothingToDo()); return; }
    setBusy(true);
    try {
      const res = await projectManagementSetProfileTaskAssignments.call(assign
        ? { profileId, templateKeys, assignProjectIds: ids }
        : { profileId, templateKeys, unassignProjectIds: ids });
      toast.success(`${assign ? strings.Assigned() : strings.Unassigned()}: ${assign ? res.assigned : res.unassigned}`);
      await loadCells(templateKeys);
      onChange?.();
    }
    finally { setBusy(false); }
  })();

  // ---- Defaults (per vertical) ---------------------------------------------
  // Only the verticals the org actually runs: a defaults list for a lifecycle with no projects is noise.
  const verticals = useMemo(
    () => uniqueArray((data?.projects || []).map((p) => p.lifecycleKey).filter(Boolean) as string[])
      .map((key) => ({ value: key, label: projectManagementLifecycles[key]?.label || key })),
    [data],
  );
  const verticalLabel = verticals.find((v) => v.value === defaultsLifecycle)?.label || defaultsLifecycle;
  // The buckets and the visible rows come from the LIFECYCLE catalogue, not data.templates: a default has to
  // be declarable against a task on no project this person belongs to yet.
  const defaultBucketList = useMemo(() => uniqueArray(defaultTemplates.map((t) => t.role).filter(Boolean) as string[]), [defaultTemplates]);
  const shownDefaultTemplates = useMemo(
    () => defaultTemplates.filter((t) => !defaultBuckets.length || (t.role != null && defaultBuckets.includes(t.role))),
    [defaultTemplates, defaultBuckets],
  );
  // Who ELSE defaults to each task — the coverage answer, given where the decision is made rather than in a
  // view of its own. Two people on one task is legal (both get it), so this informs rather than blocks.
  const othersByTemplate = useMemo(() => new Map(coverage.map((r) => [r.templateKey, r.people.filter((x) => x.profileId !== profileId)])), [coverage, profileId]);

  const loadDefaults = async (lifecycleKey: string) => {
    if (!lifecycleKey) { setDefaultTemplates([]); setCoverage([]); setDefaultKeys([]); return; }
    const [templates, cov] = await Promise.all([
      projectManagementLifecycleTemplates.call({ lifecycleKey }),
      projectManagementDefaultCoverage.call({ lifecycleKey }),
    ]);
    setDefaultTemplates(templates);
    setCoverage(cov);
    setDefaultBuckets([]);
    setDefaultKeys((data?.profile?.defaultTasks || []).filter((d) => d.lifecycleKey === lifecycleKey).map((d) => d.templateKey));
  };
  const selectDefaultsLifecycle = (key: string) => { setDefaultsLifecycle(key); loadDefaults(key); };

  // The whole vertical's list is sent on every tick — the method replaces this vertical's entries and leaves
  // the other verticals' alone, so the checkbox needs no separate save step.
  const toggleDefault = (templateKey: string, checked: boolean) => toastOnError(async () => {
    const next = checked ? uniqueArray([...defaultKeys, templateKey]) : defaultKeys.filter((k) => k !== templateKey);
    setBusy(true);
    try {
      await projectManagementSetProfileDefaults.call({ profileId, lifecycleKey: defaultsLifecycle, templateKeys: next });
      setDefaultKeys(next);
      setCoverage(await projectManagementDefaultCoverage.call({ lifecycleKey: defaultsLifecycle }));
      await load();
    }
    finally { setBusy(false); }
  })();

  const runBackfill = () => toastOnError(async () => {
    setBusy(true);
    try {
      const res = await projectManagementBackfillProfileDefaults.call({ profileId, lifecycleKey: defaultsLifecycle });
      toast.success(strings.BackfillDefaultsResult(res.tasks, res.projects));
      await load();
      onChange?.();  // memberships may have been created to carry the ownership
    }
    finally { setBusy(false); }
  })();

  // ---- Matrix (full mode) --------------------------------------------------
  const loadMatrix = async () => {
    const templates = data?.templates || [];
    const entries = await Promise.all(templates.map(async (t) => {
      const rows = await projectManagementProfileTemplateOwnership.call({ profileId, templateKey: t.templateKey });
      const m: Record<string, Cell> = {};
      for (const r of rows) m[r.projectManagementProjectId] = { taskId: r.taskId, owned: r.owned };
      return [t.templateKey, m] as const;
    }));
    setMatrix(Object.fromEntries(entries));
  };
  const toggleMatrixCell = (tmplKey: string, projectId: string, checked: boolean) => toastOnError(async () => {
    setBusy(true);
    try {
      await projectManagementSetProfileTaskAssignments.call(checked
        ? { profileId, templateKeys: [tmplKey], assignProjectIds: [projectId] }
        : { profileId, templateKeys: [tmplKey], unassignProjectIds: [projectId] });
      setMatrix((prev) => ({ ...prev, [tmplKey]: { ...prev[tmplKey], [projectId]: { ...prev[tmplKey][projectId], owned: checked } } }));
      onChange?.();
    }
    finally { setBusy(false); }
  })();
  // Whole-column select-all: over the projects that actually have this task, how many are owned / are all owned.
  const matrixColumnState = (tmplKey: string) => {
    const cells = Object.values(matrix[tmplKey] || {});
    const owned = cells.filter((c) => c.owned).length;
    return { total: cells.length, allOwned: cells.length > 0 && owned === cells.length };
  };
  const toggleMatrixColumn = (tmplKey: string, checked: boolean) => toastOnError(async () => {
    const projectIds = Object.keys(matrix[tmplKey] || {});
    if (!projectIds.length) return;
    setBusy(true);
    try {
      await projectManagementSetProfileTaskAssignments.call(checked
        ? { profileId, templateKeys: [tmplKey], assignProjectIds: projectIds }
        : { profileId, templateKeys: [tmplKey], unassignProjectIds: projectIds });
      setMatrix((prev) => {
        const col = { ...(prev[tmplKey] || {}) };
        for (const pid of Object.keys(col)) col[pid] = { ...col[pid], owned: checked };
        return { ...prev, [tmplKey]: col };
      });
      onChange?.();
    }
    finally { setBusy(false); }
  })();
  useEffect(() => { if (tab === 'matrix' && data) loadMatrix(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, data]);
  useEffect(() => { if (tab === 'defaults' && data && !defaultsLifecycle && verticals.length) selectDefaultsLifecycle(verticals[0].value); }, [tab, data, verticals]);

  if (!data) return <LoadingFlex />;

  const tabs = [
    { id: 'membership', label: strings.Membership() },
    { id: 'assignment', label: strings.TaskAssignment() },
    { id: 'defaults', label: strings.DefaultTasks() },
    ...(mode === 'full' ? [{ id: 'matrix', label: strings.AssignmentMatrix() }] : []),
  ];
  const memberProjects = (data.memberships || []).map((m) => projectById.get(m.projectManagementProjectId)).filter(Boolean) as ProjectManagementBulkProject[];

  return (
    <PartyBulkManagerStyled className={`${mode === 'full' ? 'full' : ''}${fullscreen ? ' fullscreen' : ''}`.trim() || undefined}>
      <Tooltip id="bm-tip" />
      {
        fullscreen &&
        <Icon className="bm-fs-close" icon="close" data-tooltip-id="bm-tip" data-tooltip-content={strings.ExitFullscreen()} onClick={() => setFullscreen(false)} />
      }
      <DividerTabs activeTab={tab} onTabChange={(id) => setTab(id as any)} tabs={tabs} />

      {
        tab === 'membership' &&
        <>
          <div className="bm-filters">
            <Select value={portfolioFilter} options={[{ value: '', label: strings.AllPortfolios() }, ...(data.portfolios).map((p) => ({ value: p._id, label: p.name }))]} onChange={setPortfolioFilter} />
            <Select value={stateFilter} options={[{ value: '', label: strings.AllStates() }, ...states.map((s) => ({ value: s, label: s }))]} onChange={setStateFilter} />
            <Input className="search" value={search} onChange={(v) => setSearch(v || '')} icon="search" placeholder={strings.Search()} />
          </div>
          <div className="bm-bulk">
            <Button className="secondary" disabled={busy} onClick={() => bulkMembership(true)}>{strings.AddAllFiltered()}</Button>
            <Button className="secondary" disabled={busy} onClick={() => bulkMembership(false)}>{strings.RemoveAllFiltered()}</Button>
            <span className="bm-count">{strings.MemberOfCount(memberProjectIds.size, data.projects.length)}</span>
          </div>
          <div className="bm-scroll">
            <table>
              <thead><tr><th className="check">{strings.Member()}</th><th>{strings.Project()}</th><th>{strings.Portfolio()}</th><th>{strings.State()}</th></tr></thead>
              <tbody>
                {
                  filteredProjects.map((p) => (
                    <tr key={p._id}>
                      <td className="check"><Checkbox checked={memberProjectIds.has(p._id)} disabled={busy} onChange={(c) => toggleMembership(p._id, c)} /></td>
                      <td>{p.name}{p.externalId ? <span className="pill">{p.externalId}</span> : null}</td>
                      <td className="sub">{data.portfolios.find((f) => f._id === p.portfolioId)?.name || '—'}</td>
                      <td className="sub">{p.state || '—'}</td>
                    </tr>
                  ))
                }
                {!filteredProjects.length && <tr><td colSpan={4} className="bm-empty">{strings.NoProjects()}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      }

      {
        tab === 'assignment' &&
        <>
          <div className="bm-filters">
            {
              buckets.length > 0 &&
              <RichMultiSelect
                className="bm-bucket-select"
                fixedOptions
                placeholder={strings.Stages()}
                options={buckets.map((b) => ({ value: b, label: b }))}
                selectedValues={assignBuckets}
                onChange={changeAssignBuckets}
              />
            }
            <RichMultiSelect
              className="bm-tmpl-select"
              fixedOptions
              placeholder={strings.SelectTaskTemplate()}
              options={assignTemplates.map((t) => ({ value: t.templateKey, label: t.label }))}
              selectedValues={templateKeys}
              onChange={(vals) => selectTemplates(vals)}
            />
            {
              templateKeys.length > 0 &&
              <>
                <Button className="secondary" disabled={busy} onClick={() => bulkAssign(true)}>{strings.AssignAllMembers()}</Button>
                <Button className="secondary" disabled={busy} onClick={() => bulkAssign(false)}>{strings.UnassignAllMembers()}</Button>
              </>
            }
          </div>
          {
            !data.templates.length
              ? <div className="bm-empty">{strings.NoMemberProjects()}</div>
              : !templateKeys.length
                ? <div className="bm-empty">{strings.SelectTaskTemplateHint()}</div>
                : <div className="bm-scroll">
                  <table>
                    <thead><tr><th className="check">{strings.Owner()}</th><th>{strings.Project()}</th><th>{strings.Assigned()}</th></tr></thead>
                    <tbody>
                      {
                        memberProjects.map((p) => {
                          const st = projectAssignState(p._id);
                          return (
                            <tr key={p._id}>
                              <td className="check">
                                {st.present ? <Checkbox checked={st.allOwned} disabled={busy} onChange={(c) => toggleAssignment(p._id, c)} /> : <span className="muted">—</span>}
                              </td>
                              <td>{p.name}{p.externalId ? <span className="pill">{p.externalId}</span> : null}</td>
                              <td className="sub">{st.present ? `${st.owned} / ${st.present}` : <span className="muted">{strings.NoSuchTask()}</span>}</td>
                            </tr>
                          );
                        })
                      }
                      {!memberProjects.length && <tr><td colSpan={3} className="bm-empty">{strings.NoMemberProjects()}</td></tr>}
                    </tbody>
                  </table>
                </div>
          }
        </>
      }

      {
        tab === 'defaults' &&
        (
          !verticals.length
            ? <div className="bm-empty">{strings.NoProjects()}</div>
            : <div className="bm-defaults">
              <div className="bm-filters">
                <Select className="bm-vertical-select" value={defaultsLifecycle} options={verticals} onChange={selectDefaultsLifecycle} />
                {
                  defaultBucketList.length > 0 &&
                  <RichMultiSelect
                    className="bm-bucket-select"
                    fixedOptions
                    placeholder={strings.Stages()}
                    options={defaultBucketList.map((b) => ({ value: b, label: b }))}
                    selectedValues={defaultBuckets}
                    onChange={setDefaultBuckets}
                  />
                }
                <Button className="secondary" disabled={busy || !defaultKeys.length} onClick={() => setConfirmBackfill(true)}>{strings.BackfillNow()}</Button>
                <span className="bm-count">{strings.TasksCount(defaultKeys.length)}</span>
              </div>
              <p className="bm-hint">{strings.DefaultTasksHint(data.profile?.name || '', verticalLabel)}</p>
              {!defaultKeys.length && <p className="bm-hint">{strings.DefaultTasksEmpty()}</p>}
              <div className="bm-scroll">
                <table>
                  <thead><tr><th className="check">{strings.Default()}</th><th>{strings.Task()}</th><th>{strings.Stages()}</th><th>{strings.AlsoDefaultFor()}</th></tr></thead>
                  <tbody>
                    {
                      shownDefaultTemplates.map((t) => {
                        const others = othersByTemplate.get(t.templateKey) || [];
                        return (
                          <tr key={t.templateKey}>
                            <td className="check">
                              <Checkbox checked={defaultKeys.includes(t.templateKey)} disabled={busy} onChange={(c) => toggleDefault(t.templateKey, c)} />
                            </td>
                            <td className="task">{t.label}</td>
                            <td className="sub">{t.role || '—'}</td>
                            <td className="others">{others.length ? others.map((o) => o.name).join(', ') : <span className="muted">—</span>}</td>
                          </tr>
                        );
                      })
                    }
                    {!shownDefaultTemplates.length && <tr><td colSpan={4} className="bm-empty">{strings.NoSuchTask()}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
        )
      }

      {
        tab === 'matrix' &&
        (
          !memberProjects.length || !data.templates.length
            ? <div className="bm-empty">{strings.NoMemberProjects()}</div>
            : <>
              <div className="bm-matrix-tools">
                {
                  buckets.length > 0 &&
                  <RichMultiSelect
                    className="bm-matrix-bucket-select"
                    fixedOptions
                    placeholder={strings.Stages()}
                    options={buckets.map((b) => ({ value: b, label: b }))}
                    selectedValues={matrixBuckets}
                    onChange={setMatrixBuckets}
                  />
                }
                {!fullscreen && <Icon className="bm-fs-expand" icon="openInNew" data-tooltip-id="bm-tip" data-tooltip-content={strings.ExpandFullscreen()} onClick={() => setFullscreen(true)} />}
              </div>
              <div className="matrix-scroll">
                <table className="matrix">
                  <thead>
                    <tr>
                      <th>{strings.Project()}</th>
                      {matrixTemplates.map((t) => <th key={t.templateKey} className="tmpl check">{t.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="select-all-row">
                      <td className="proj">{strings.SelectAll()}</td>
                      {
                        matrixTemplates.map((t) => {
                          const cs = matrixColumnState(t.templateKey);
                          return (
                            <td key={t.templateKey} className="check">
                              {cs.total ? <Checkbox checked={cs.allOwned} disabled={busy} onChange={(c) => toggleMatrixColumn(t.templateKey, c)} /> : <span className="muted">·</span>}
                            </td>
                          );
                        })
                      }
                    </tr>
                    {
                      memberProjects.map((p) => (
                        <tr key={p._id}>
                          <td className="proj">{p.name}{p.externalId ? <span className="pill">{p.externalId}</span> : null}</td>
                          {
                            matrixTemplates.map((t) => {
                              const cell = matrix[t.templateKey]?.[p._id];
                              return (
                                <td key={t.templateKey} className="check">
                                  {cell ? <Checkbox checked={cell.owned} disabled={busy} onChange={(c) => toggleMatrixCell(t.templateKey, p._id, c)} /> : <span className="muted">·</span>}
                                </td>
                              );
                            })
                          }
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </>
        )
      }

      {
        confirmBackfill &&
        <ConfirmModal
          title={strings.BackfillNow()}
          body={strings.BackfillDefaultsConfirm(verticalLabel)}
          onConfirm={() => { setConfirmBackfill(false); runBackfill(); }}
          onClose={() => setConfirmBackfill(false)}
        />
      }

      {
        confirmRemove &&
        <ConfirmModal
          title={strings.RemoveAllFiltered()}
          body={strings.RemoveMembershipsConfirm(confirmRemove.length)}
          onConfirm={() => { const ids = confirmRemove; setConfirmRemove(undefined); applyMembershipDelta(false, ids); }}
          onClose={() => setConfirmRemove(undefined)}
        />
      }
    </PartyBulkManagerStyled>
  );
};
